import type { VisualSignalFrame } from '../signals/VisualSignal.js';
import type { VisualMutation } from './VisualMutation.js';

/**
 * How the artwork is doing, which is not how the player is doing.
 *
 * This is the one piece of Phase 3 most at risk of becoming a second scoreboard,
 * so it is worth being precise about what it is not. `stability` is not accuracy
 * renamed. `chaos` is not a miss counter. A run can end with a poor score and a
 * highly ordered picture — steady, late, consistent tapping — or with a perfect
 * score and a violent one, if the player kept hammering between notes.
 *
 * What makes that true is that these fields are fed by *behaviour*, not by
 * grades: how regular the presses were, how much silence there was, how often
 * the player struck where nothing was. The judge's opinion is one input among
 * several and never the only one.
 *
 * Everything accumulates and decays. That is the point of §11: a single miss is
 * a moment, three misses in ten seconds is a condition, and the difference has
 * to be visible in the picture. Decay is per-second and frame-rate independent,
 * so a slow machine reaches the same state as a fast one.
 */

export interface ArtStateValues {
  /** How much is happening. Rises with input and amplitude. */
  readonly energy: number;
  /** Disorder. Misses, off-beat presses and breaks feed this. */
  readonly chaos: number;
  /** Regularity of the piece. Falls when the player strikes off-grid. */
  readonly symmetry: number;
  /** How full the frame is. Driven by sustained activity and bass. */
  readonly density: number;
  /** Accumulated damage. This is the one that makes failure *material*. */
  readonly distortion: number;
  /** Continuity — long unbroken stretches, of any accuracy. */
  readonly flow: number;
  /** Settledness. Rises during quiet, falls on every disturbance. */
  readonly stability: number;
}

const FIELDS = [
  'energy',
  'chaos',
  'symmetry',
  'density',
  'distortion',
  'flow',
  'stability',
] as const;

export type ArtStateField = (typeof FIELDS)[number];

/** Seconds for a field to fall back to its rest value from full. */
const DECAY_SECONDS: Readonly<Record<ArtStateField, number>> = {
  energy: 2.5,
  chaos: 9,
  symmetry: 12,
  density: 4,
  // The longest by far: damage is meant to be the thing a run carries with it.
  distortion: 26,
  flow: 6,
  stability: 10,
};

/** Where each field settles when nothing is happening. */
const REST: Readonly<Record<ArtStateField, number>> = {
  energy: 0,
  chaos: 0,
  symmetry: 1,
  density: 0,
  distortion: 0,
  flow: 0,
  stability: 1,
};

export class ArtState {
  private readonly values: Record<ArtStateField, number>;
  /** Song time of the last press, for the silence and rapid-input rules. */
  private lastInputTime: number | null = null;
  private recentInputs = 0;

  constructor() {
    this.values = { ...REST };
  }

  get current(): ArtStateValues {
    return { ...this.values };
  }

  read(field: ArtStateField): number {
    return this.values[field];
  }

  reset(): void {
    for (const field of FIELDS) this.values[field] = REST[field];
    this.lastInputTime = null;
    this.recentInputs = 0;
  }

  /**
   * Pulls every field back towards rest, and folds in the continuous signals.
   *
   * Called once a frame by the engine, before renderers read it.
   */
  advance(deltaSeconds: number, signals: VisualSignalFrame, songTime: number): void {
    const delta = deltaSeconds > 0 ? deltaSeconds : 0;

    for (const field of FIELDS) {
      const rest = REST[field];
      const step = delta / DECAY_SECONDS[field];
      const value = this.values[field];
      this.values[field] = value > rest ? Math.max(rest, value - step) : Math.min(rest, value + step);
    }

    // Loudness is energy and fullness directly — this is what makes AUTO mode
    // produce a moving picture with nobody playing.
    this.raise('energy', signals.AMPLITUDE * delta * 1.6);
    this.raise('density', signals.BASS * delta * 0.9);
    this.raise('energy', signals.ONSET * delta * 2.2);

    // Silence settles the piece. Not "the player is doing badly" — a held pause
    // is a legitimate thing to do to an artwork, and it should read as calm.
    if (this.lastInputTime !== null && songTime - this.lastInputTime > 3) {
      this.raise('stability', delta * 0.22);
      this.raise('symmetry', delta * 0.14);
      this.lower('chaos', delta * 0.1);
    }

    // Rapid input decays back to nothing so a burst is a burst, not a mode.
    this.recentInputs = Math.max(0, this.recentInputs - delta * 2.5);
  }

  /**
   * Folds one mutation into the state.
   *
   * The engine calls this for every mutation it dispatches, whatever renderers
   * do with it, so the artwork's state reflects what was *asked for* rather than
   * what any one layer happened to implement.
   */
  applyMutation(item: VisualMutation): void {
    // Sustained mutations arrive every frame; they must not accumulate like a
    // one-shot or a held combo would peg every field within a second.
    const weight = item.sustained ? 0.004 : 1;
    const force = item.strength * weight;

    switch (item.action) {
      case 'DISTORT':
      case 'BREAK':
        this.raise('distortion', force * 0.16);
        this.raise('chaos', force * 0.12);
        this.lower('stability', force * 0.18);
        this.lower('symmetry', force * 0.1);
        break;
      case 'SPLASH':
      case 'BLEED':
        this.raise('distortion', force * 0.1);
        this.raise('energy', force * 0.3);
        this.lower('symmetry', force * 0.14);
        break;
      case 'TURBULENCE':
        this.raise('chaos', force * 0.2);
        this.lower('stability', force * 0.2);
        break;
      case 'SPAWN':
        this.raise('energy', force * 0.25);
        this.raise('density', force * 0.12);
        this.raise('flow', force * 0.08);
        break;
      case 'EXPAND':
      case 'DENSITY':
        this.raise('density', force * 0.18);
        break;
      case 'CONTRACT':
        this.lower('density', force * 0.12);
        break;
      case 'ROTATE':
      case 'SHIFT':
        this.raise('flow', force * 0.14);
        break;
      case 'MERGE':
        this.raise('symmetry', force * 0.12);
        this.raise('stability', force * 0.1);
        break;
      case 'FREEZE':
        this.raise('stability', force * 0.2);
        this.lower('energy', force * 0.2);
        break;
      case 'RELEASE':
      case 'RANDOMIZE':
        this.raise('chaos', force * 0.16);
        break;
      default:
        // OPACITY, INTENSITY, FADE, REMOVE and anything added later are
        // presentation rather than condition: they change how a layer looks
        // without changing what the piece has been through.
        break;
    }
  }

  /**
   * A press, judged or not.
   *
   * The three cases the brief names — normal, off-beat and rapid — are separated
   * here rather than in a renderer, because they are facts about the run and
   * every layer should be able to agree on them.
   */
  registerInput(songTime: number, judged: boolean, onBeatDistance: number): void {
    const gap = this.lastInputTime === null ? Number.POSITIVE_INFINITY : songTime - this.lastInputTime;
    this.lastInputTime = songTime;
    this.recentInputs += 1;

    this.raise('energy', 0.12);

    if (gap < 0.12) {
      // Hammering. Loud, disordered, and not a failure — a burst is a mark.
      this.raise('chaos', 0.06 + Math.min(0.12, this.recentInputs * 0.012));
      this.raise('energy', 0.1);
      this.lower('stability', 0.05);
    }

    if (!judged) {
      // Struck where nothing was. Pulls the piece off-axis rather than damaging
      // it: an off-beat press is a decision, not a mistake.
      this.lower('symmetry', 0.05);
      this.raise('chaos', 0.03);
      this.raise('energy', 0.05);
      return;
    }

    // A press near the grid tightens the piece; one far from it loosens it.
    // Distance, not grade, so a consistently late player still builds order.
    const offGrid = Math.min(1, onBeatDistance / 0.25);
    this.raise('flow', 0.05 * (1 - offGrid));
    this.raise('symmetry', 0.03 * (1 - offGrid));
    this.lower('symmetry', 0.04 * offGrid);
  }

  /** The run's continuity broke. Distinct from a miss: this is the *streak*. */
  registerComboBreak(): void {
    this.raise('chaos', 0.18);
    this.raise('distortion', 0.12);
    this.lower('flow', 0.55);
    this.lower('stability', 0.22);
  }

  private raise(field: ArtStateField, amount: number): void {
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.values[field] = Math.min(1, this.values[field] + amount);
  }

  private lower(field: ArtStateField, amount: number): void {
    if (!Number.isFinite(amount) || amount <= 0) return;
    this.values[field] = Math.max(0, this.values[field] - amount);
  }
}
